'use strict';

/**
 * Do not statically require Router. this is used by runtimeEnvironment, and must not load JET until needed.
 * One reason is, the translation bundle loads are funnelled through runtimeEnvironment,
 * and the (requireJS) configuration for bundles must be done before JET is loaded.
 * If anything in runtimeEnvironment loads JET through a module dependency,
 * it will be loaded before the bootstrap has had a chance to initialize ConfigLoader (which config the bundles).
 */
define('vb/private/services/servicesLoader',[
  'signals',
  'urijs/URI',
  'vb/private/configLoader',
  'vb/private/constants',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/services/serviceConstants',
  'vb/private/services/serviceUtils',
  'vb/private/services/swaggerUtils',
], (
  signals,
  URI,
  ConfigLoader,
  Constants,
  Log,
  Utils,
  ServiceConstants,
  ServiceUtils,
  SwaggerUtils,
) => {
  const logger = Log.getLogger('/vb/private/services/servicesLoader');

  const EXTENSION_PREFIX = `${Constants.EXTENSION_PATH_NAME}/`;

  const SERVICE_LOAD_TIMEOUT = (() => {
    let timeout = Constants.Services.definitionTimeout;

    const storageOverride = globalThis.localStorage && globalThis.localStorage.getItem('vb.services.load-timeout');
    if (storageOverride) {
      try {
        const storageNumber = Number.parseInt(storageOverride, 10);
        if (storageNumber && !Number.isNaN(storageNumber)) {
          timeout = storageNumber * 1000;
          return timeout;
        }
      // eslint-disable-next-line no-empty
      } catch (err) {}
    }

    const vbConfigOverride = globalThis.vbInitConfig && globalThis.vbInitConfig.SERVICE_LOAD_TIMEOUT;
    if (vbConfigOverride && !Number.isNaN(vbConfigOverride)) {
      timeout = vbConfigOverride * 1000;
    }

    return timeout;
  })();

  class ServicesLoader {
    /**
     * This method is used (instead of requireJS) for two cases:
     * 1) we need additional Request configuration, like headers, to make the request (not supported by requireJS)
     * 2) using requireJS has failed, for some reason; this is only used as a fallback in this case.
     *
     * note: 'path' used to have to be prepended with Router.baseUrl by the caller;
     * this has changed for 19.1.3, we will prepend it here
     *
     * @param {string} path
     * @param {Object} requestInit an object usable as the 'init' for a Request constructor
     * @returns {Promise<{data: Object, headers: Headers}>} resolved with the definition (as an object) and headers,
     *                                                      or rejected
     */
    static loadWithFetchOrXHR(path, requestInit = {}) {
      return Utils.getResource('vb/private/stateManagement/router')
        .then((Router) => {
          // if we are using fetch() and the path is NOT absolute, we need to prepend the Router.baseUrl
          const isAbsolutePath = Utils.isAbsolutePath(path);
          const url = isAbsolutePath ? path : Router.baseUrl + path;

          // eslint-disable-next-line no-param-reassign
          requestInit.resolvedUrl = url;

          const init = Object.assign({}, requestInit, { credentials: 'same-origin' });
          const request = new Request(url, init);

          return fetch(request);
        })
        .then((response) => {
          if (response.ok) {
            return Promise.all([response, response.json()]);
          }
          throw Error(`service fetch failed: ${path} : ${response.status} ${response.statusText}`);
        })
        .then(([response, data]) => ({
          data,
          headers: response.headers,
        }));
    }

    /**
     * changed for 18.4.3
     *
     * this method will use either fetch() or require().
     *
     * requireJS will be used if
     *  a) there is no additional request configuration (which currently means no 'headers') for the service def, AND
     *  b) the path to the service does not include an explicit protocol.
     *
     * Example of (a)...it is possible to get OpenAPI 3 for a Business Object using:
     *    Accept: application/vnd.oracle.openapi3+json
     *
     * So, if there are no headers or additional Request properties, and no protocol, use requireJS.
     * Otherwise, use fetch().
     *
     * if requireJS is used, the timeout is ignored, and the requireJS timeout applies (typically 7 seconds).
     *
     * fetch isn't cancelled when elapsed time reached 'timeout', we just don't wait anymore, and Promise rejects.
     *
     * (originally introduced with the change to use fetch() to get service definitions, because of timeouts)
     *
     * originally tried to put into Utils, but that caused a dependency loop:
     *    Utils -> Router -> Log -> Utils -> ...
     * @param url
     * @param requestInit
     * @param timeout defaults to Constants.defaultFetchTimeout (30 secs)
     * @returns {Promise<{data: Object, headers: Headers}>} resolved with the definition (as an object) and headers,
     *                                                      or rejected
     */
    // NOTE: this is used by DT: DtRuntimeEnvironment. see BUFP-34538
    static loadWithTimeout(url, requestInit, timeout = SERVICE_LOAD_TIMEOUT) {
      // don't count vb-info-extension as a header
      const hasInfoExtensionHeader = requestInit.headers
        && requestInit.headers[Constants.Headers.VB_INFO_EXTENSION];

      const hasHeaders = requestInit.headers
        && Object.keys(requestInit.headers).length > (hasInfoExtensionHeader ? 1 : 0);

      // 'hasInitProps' means, there are additional properties for the Request, other than headers
      const hasInitProps = (Object.keys(requestInit).length > (requestInit.headers ? 1 : 0));

      function loadNotUsingRequireJS() {
        const timeoutError = new Error(`Service definition loading timed out: ${timeout}`);
        return Utils.promiseRaceWithTimeout(ServicesLoader.loadWithFetchOrXHR(url, requestInit), timeout, timeoutError);
      }

      if (!hasHeaders && !hasInitProps) {
        const urlInfo = URI.parse(url);
        if (!urlInfo.protocol) {
          return ServicesLoader.loadWithRequireJs(url, requestInit)
            .catch((err) => {
              // if requireJS fails with a timeout, use fetch()
              if (err && err.requireType === 'timeout') {
                logger.warn(`Fetching service with requireJS timed out after ${timeout}ms, trying fetch:`, url, err);
                return loadNotUsingRequireJS();
              }
              throw err;
            });
        }
      }

      return loadNotUsingRequireJS();
    }

    /**
     * use require 'text' plugin (which we have configured to use XHR)
     * @param url
     * @param requestInit
     * @returns {Promise<{data: Object, headers: Headers}>} resolved with the definition (as an object) and headers,
     *                                                      or rejected
     */
    static loadWithRequireJs(url, requestInit = {}) {
      // eslint-disable-next-line no-param-reassign
      requestInit.resolvedUrl = url;

      let loadPromise;
      // need to determine if its an extension
      if (url.startsWith(EXTENSION_PREFIX)) {
        loadPromise = Utils.getRuntimeEnvironment().then((re) => re.getExtensionTextResource(url));
      } else {
        loadPromise = Utils.getTextResource(url);
      }

      return loadPromise
        .then((def) => ({ data: SwaggerUtils.parseServiceText(def), headers: new Headers() }));
    }

    /**
     * look up the url in the catalog, and return an object with any extension information found:
     * {
     *   url: the resolved url to use
     *   services: {
     *        - an object to use for initializing a Request object for fetch()ing the service def.
     *        - may be empty, the caller may decide to use something other than fetch().
     *        - note: this is for getting the service def, and NOT for calls made to the
     *      extensions: {
     *         headers: {
     *            - Currently only contains headers, and is merged with the 'existingHeaders' param.
     *            - endpoints in the def, a.k.a 'backends'.
     *         }
     *      }
     *   },
     *   backends: {
     *      extensions: {
     *         - any additional properties that should override or merge with any existing 'x-vb' extensions
     *         - for the 'backend' calls (and NOT for the call to get the the service def itself).
     *      }
     *   }
     * }
     *
     * During URL resolution this method will trigger url "opened" signal, which will update URLMapper.
     *
     * @param protocolRegistry
     * @param url
     * @param serviceName typically the service name; added to the vb-info-extension, and used by the plugins
     *         to construct a proxy url, if needed.
     * @param namespace {string}
     * @param {object} [serverVariables] - the serverVariables, if any.
     * @param {object} [serviceDefHeaders] - any headers associated with the service declaration
     *                                       before catalog resolution, if any. unused for endpoint resolution.
     * @param {boolean} [skipTrapExtensions] do not include TRAP proxy/tokenRelay URLs in the vb-info-extension header
     * @param {boolean} [useUrlMapping] apply url mapping headers if service url is not found in catalogs
     * @returns {Promise} { url, services: { extensions }, backends: { extensions } }
     */
    static async getCatalogExtensions(protocolRegistry, url, serviceName, namespace,
      serverVariables,
      serviceDefHeaders,
      skipTrapExtensions,
      useUrlMapping) {
      const resolved = await protocolRegistry.getResolvedInfoOrDefault(url, namespace, serverVariables);
      let name = serviceName;

      // do not override extensions if found, only if we got empty default values
      if (resolved.default && useUrlMapping) {
        const mapping = await ConfigLoader.urlMapper.getUrlMapping(url, true);
        if (mapping) {
          // Remove extra URL mapping properties
          // remove trapExtension
          delete mapping.resolvedProxyUrl;
          delete mapping.resolvedTokenRelayUrl;
          delete mapping.trapEnabled;
          //
          delete mapping.url;
          delete mapping.urlToFetch;

          if (mapping.type === ServiceConstants.ExtensionTypes.SERVICES) {
            resolved.extensions.services = mapping;

            // ProtocolRegistry folds all extensions into the backends object,
            // and that is where ServiceDefinition reads out from.
            // [search for servicesExtWithoutHeaders in ProtocolRegistry]
            // We need to mimic that behavior here.
            if (mapping.transforms) {
              resolved.extensions.backends.transforms = mapping.transforms;
              delete mapping.transforms;
            }
            if (mapping.authentication) {
              resolved.extensions.backends.authentication = mapping.authentication;
            }
          } else if (mapping.type === ServiceConstants.ExtensionTypes.BACKENDS) {
            resolved.extensions.backends = mapping;
          }
          // else - warn

          // url map data uses 'serviceName' property for both service and backend names
          name = name || mapping.serviceName;
          mapping.name = name;

          resolved.chain[0] = {
            type: mapping.type,
            name: mapping.name,
            namespace: mapping.namespace,
            version: mapping.version,
            serviceType: mapping.serviceType,
            // do we need: extensionAccess or variables?
          };
          resolved.type = mapping.type;
          resolved.namespace = mapping.namespace;
          resolved.version = mapping.version;

          delete mapping.type;
        }
      }

      const catalogExt = ServiceUtils
        .transformResolvedCatalogObject(resolved, name, url, serviceDefHeaders || {}, skipTrapExtensions);

      return catalogExt;
    }

    /**
     * Sends a signal to the ServicesLoader.opened signal subscribers.
     * Currently it is only UrlMapper that is interested in this, so it can update its
     * reverse URL mapping data.
     *
     * @param serviceDefinition
     * @param serverVariables
     * @returns {*}
     */
    static notify(serviceDefinition, serverVariables) {
      const id = serviceDefinition.name;
      const nameForProxy = serviceDefinition.nameForProxy || id;
      const servers = serviceDefinition.getServers();

      const ext = serviceDefinition.extensions;
      const catalogInfo = serviceDefinition.catalogInfo;
      // include the 'metadata', for when the service is really coming from the catalog indirection.
      const metadata = (catalogInfo && catalogInfo.services && catalogInfo.services.metadata) || {};

      const urlInfos = servers.map((server) => Object.assign(
        {
          url: server.getUrl(serverVariables),
          id,
          nameForProxy,
          metadata,
        },
        ext,
      ));

      return ServicesLoader.opened.dispatch(id, urlInfos, serviceDefinition.namespace);
    }
  }

  ServicesLoader.opened = new signals.Signal();

  return ServicesLoader;
});

